import React, { memo } from 'react'
import styled from 'styled-components'

import { PairLink } from '@root/entities/pair/ui/common'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'
import { FadeOverflow } from '@root/shared/ui-kit'

import { TradingPairExploreItemMetricsSummarySmall } from './metrics/pair-explore-item-metrics-summary-small'
import { PairExploreItemActions } from './pair-explore-item-actions'
import { PairExploreItemAudit } from './pair-explore-item-audit'
import { PairExploreItemChartTooltip } from './pair-explore-item-chart-tooltip'
import { PairExploreItemHeading } from './pair-explore-item-heading'
import { PairExploreItemIcon } from './pair-explore-item-icon'

import type { PairExploreItemBodyProps } from './pair-explore-item-types'
import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = PairExploreItemBodyProps & ClassNameProps

export const PairExploreItemBodySmall: React.FC<Props> = memo(
  ({ pair, statsPeriod, priceChartInterval, onClick, iconSrcOverride }) => {
    return (
      <Root>
        <PairExploreBackgroundLink
          pair={pair}
          onClick={() => onClick?.({ pair })}
        />
        <Top>
          <Side>
            <PairExploreItemIcon
              pair={pair}
              size={76}
              iconSrcOverride={iconSrcOverride}
            />
          </Side>
          <Body>
            <Heading>
              <UpperGroup>
                <PairExploreItemHeading pair={pair} />
                <PairExploreItemActions pair={pair} />
              </UpperGroup>
              <BuyButton
                pair={pair}
                withAmount
                withLabel={false}
                section="exploreFeeds"
              />
            </Heading>
            <TradingPairExploreItemMetricsSummarySmall
              pair={pair}
              statsPeriod={statsPeriod}
            />
          </Body>
        </Top>
        <LowerGroup>
          <FadeOverflow inlineStart={0}>
            <PairExploreItemAudit pair={pair} size={14} />
          </FadeOverflow>
          <ChartButton pair={pair} interval={priceChartInterval} />
        </LowerGroup>
      </Root>
    )
  },
)

const Root = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  padding-inline: 10px;
  padding-block: 10px;
`

const Body = styled.div`
  pointer-events: none;
  position: relative;
  display: grid;
  gap: 3px;
`

const Top = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 10px;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px;
`

const LowerGroup = styled.div`
  pointer-events: none;
  z-index: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px;
  align-items: center;
`

const UpperGroup = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 2px;
`

const BuyButton = styled(QuickBuyButton)``

const Side = styled.div`
  position: relative;
  display: grid;
  justify-items: center;
  align-items: center;
  gap: 8px;
`

const ChartButton = styled(PairExploreItemChartTooltip)`
  margin-top: 0;
`
const PairExploreBackgroundLink = styled(PairLink<PairModel>)`
  position: absolute;
  inset: 0;
`
